// ============================================
// ENTITLEMENT CONSTANTS — single TS source of truth
// ============================================
//
// MIRROR: functions-enough/common/entitlements.py
// The capability sets and priority map below must stay identical to the
// Python module. Change them together.
//
// ONE PRODUCT (2026-10-07). enough sells a single membership — `spending_tools`
// at $15/mo or $100/yr — and every paid key grants everything: data connections,
// the spending tools (Shopping Break, Subscription Finder, Pre-purchase Pause
// vault, unlimited chat and accounts) AND the debt tools (debt goals, the debt
// plan). The 2026-07 two-line model (spending_tools ↔ debt_tools orthogonal,
// all_tools = both) is retired: nothing ever sold `debt_tools` or `all_tools`,
// no prod user holds them, and the core-loop redesign (decision 6) put debt
// tools inside the $15 product.
//
// The keys are still HONORED — a RevenueCat grant of `debt_tools` or `all_tools`
// resolves paid — and the three capability predicates keep their names so call
// sites read as what they gate, but they are the same set by design.
//
// Legacy keys: data_sync (the level actually used in production) and
// early_access (no active holders) are paid keys like any other; isEarlyAccess
// is preserved for any existing early_access-gated surface. (Legacy Payoff
// Genius 'pro' was fully retired 2026-07: not a key, not honored.)

import type { EntitlementId, ProductLine } from '../types/subscriptions';

/** Higher number = higher tier; picks the doc's single `status` string. NOT a
 *  capability grant — capabilities come from the sets below. */
export const ENTITLEMENT_PRIORITY: Record<EntitlementId, number> = {
  all_tools: 6,
  early_access: 5, // legacy paid tier → spending equivalent
  debt_tools: 4,
  spending_tools: 3,
  data_sync: 1, // legacy → spending equivalent
};

/** Every paid key. One product: each of these grants everything. */
export const PAID_ENTITLEMENTS: ReadonlySet<string> = new Set([
  'spending_tools',
  'all_tools',
  'debt_tools',
  'data_sync',
  'early_access',
]);

/** Entitlements that grant the spending tools — every paid key. */
export const SPENDING_TOOLS_ENTITLEMENTS: ReadonlySet<string> = PAID_ENTITLEMENTS;

/** Entitlements that grant the debt tools — every paid key (since 2026-10-07;
 *  before that only debt_tools / all_tools). */
export const DEBT_TOOLS_ENTITLEMENTS: ReadonlySet<string> = PAID_ENTITLEMENTS;

/** Entitlements that grant data connections (Plaid/Spinwheel sync, unlimited
 *  accounts) — every paid key. */
export const DATA_CONNECT_ENTITLEMENTS: ReadonlySet<string> = PAID_ENTITLEMENTS;

/**
 * Fixed monthly prices per product line (dollars).
 * MIRROR: PRODUCT_LINE_PRICES in functions-enough/common/entitlements.py —
 * change them together. One product, one price: the 2026-07 paid-launch
 * price (PAID_LAUNCH_PLAN.md §4a: $15/mo, $100/yr). `debt` and `all` are
 * retired lines nothing sells; they price as the one product so a legacy
 * `debt_tools` / `all_tools` grant never reports a different number.
 */
export const PRODUCT_LINE_PRICES: Record<ProductLine, number> = {
  spending: 15,
  debt: 15,
  all: 15,
};

/**
 * The product line an entitlement id is priced as. Legacy `data_sync` /
 * `early_access` price as Spending Tools (the line they grant). Unknown ids —
 * including anything that isn't a paid key — return null.
 */
export const productLineForEntitlement = (id: string): ProductLine | null => {
  if (id === 'all_tools') return 'all';
  if (id === 'debt_tools') return 'debt';
  if (SPENDING_TOOLS_ENTITLEMENTS.has(id)) return 'spending';
  return null;
};

/**
 * Fixed ANNUAL prices per product line (dollars). The 2026-07 paid-launch
 * annual price (PAID_LAUNCH_PLAN.md §4a: $15/mo or $100/yr, 44% saving) — the
 * same number the web paywall reads from VITE_STRIPE_SPENDING_ANNUAL_PRICE_ID.
 * `debt` / `all` are retired lines priced as the one product (see
 * PRODUCT_LINE_PRICES). null would mean "we cannot price this annually" —
 * callers must show nothing rather than guess (see annualPriceForEntitlements).
 *
 * Display-only today, so there is deliberately no Python mirror; if the server
 * ever prices annual subscriptions, mirror it into
 * functions-enough/common/entitlements.py alongside PRODUCT_LINE_PRICES.
 */
export const PRODUCT_LINE_ANNUAL_PRICES: Record<ProductLine, number | null> = {
  spending: 100,
  debt: 100,
  all: 100,
};

/**
 * The product line a set of entitlement ids is billed for — at most ONE.
 * There is one product, so holding several paid keys (a legacy all_tools plus
 * data_sync, say) is one membership priced once, never a sum. `all` wins over
 * the single lines only so the label matches the highest key held.
 */
const billableLines = (ids: readonly string[]): ProductLine[] => {
  const lines = new Set<ProductLine>();
  for (const id of ids) {
    const line = productLineForEntitlement(id);
    if (line) lines.add(line);
  }
  if (lines.size === 0) return [];
  if (lines.has('all')) return ['all'];
  return [lines.has('spending') ? 'spending' : 'debt'];
};

/**
 * List-price monthly dollars for a set of active entitlement ids, or null when
 * none of them is a paid key (free / unknown).
 *
 * Every number comes from PRODUCT_LINE_PRICES — never copy a price into a
 * component. This is the LIST price: it does not know about legacy $4–12
 * slider subscriptions or comped grants, so callers that display money must
 * prefer an explicitly billed amount when they have one, and must not render
 * it for a comped (promotional) subscription.
 */
export const monthlyPriceForEntitlements = (ids: readonly string[]): number | null => {
  const lines = billableLines(ids);
  if (lines.length === 0) return null;
  let total = 0;
  for (const line of lines) total += PRODUCT_LINE_PRICES[line];
  return total;
};

/**
 * List-price ANNUAL dollars for a set of active entitlement ids, or null when
 * none of them is a paid line OR any line has no annual price. Null is a hard
 * "we don't know" — a caller displaying money must render nothing rather than
 * fall back to a monthly number, which would misstate what the member pays.
 */
export const annualPriceForEntitlements = (ids: readonly string[]): number | null => {
  const lines = billableLines(ids);
  if (lines.length === 0) return null;
  let total = 0;
  for (const line of lines) {
    const price = PRODUCT_LINE_ANNUAL_PRICES[line];
    if (price === null) return null;
    total += price;
  }
  return total;
};

export const hasSpendingTools = (ids: readonly string[]): boolean =>
  ids.some((id) => SPENDING_TOOLS_ENTITLEMENTS.has(id));

export const hasDebtTools = (ids: readonly string[]): boolean =>
  ids.some((id) => DEBT_TOOLS_ENTITLEMENTS.has(id));

export const hasDataConnect = (ids: readonly string[]): boolean =>
  ids.some((id) => DATA_CONNECT_ENTITLEMENTS.has(id));

/** Known RC lookup_keys, sorted highest-priority first; unknown ids dropped. */
export const knownEntitlements = (ids: readonly string[]): EntitlementId[] =>
  ids
    .filter((id): id is EntitlementId => (id as EntitlementId) in ENTITLEMENT_PRIORITY)
    .sort((a, b) => ENTITLEMENT_PRIORITY[b] - ENTITLEMENT_PRIORITY[a]);

/** The minimal shape needed to resolve a customer record's active entitlements. */
export interface EntitlementSource {
  activeEntitlementIds?: readonly string[] | null;
  entitlementLevel?: string | null;
  status?: string | null;
  hasActiveSubscription?: boolean | null;
}

/**
 * Resolve a customer record to its active entitlement ids, priority-sorted.
 * Mirrors functions-enough/common/entitlements.py resolve_entitlement_ids so
 * the client gates agree with the server (incl. the F1 skewed-doc repair:
 * hasActiveSubscription + status but an empty array still resolves paid).
 */
export const resolveEntitlementIds = (data?: EntitlementSource | null): EntitlementId[] => {
  if (!data) return [];
  const known = knownEntitlements(data.activeEntitlementIds ?? []);
  if (known.length) return known;
  const level = data.entitlementLevel;
  if (level && level in ENTITLEMENT_PRIORITY) return [level as EntitlementId];
  const status = data.status;
  if (data.hasActiveSubscription && status && status in ENTITLEMENT_PRIORITY) {
    return [status as EntitlementId];
  }
  return [];
};

export const highestEntitlement = (ids: readonly string[]): EntitlementId | null => {
  let best: EntitlementId | null = null;
  let bestPriority = 0;
  for (const id of ids) {
    const priority = ENTITLEMENT_PRIORITY[id as EntitlementId];
    if (priority !== undefined && priority > bestPriority) {
      best = id as EntitlementId;
      bestPriority = priority;
    }
  }
  return best;
};

export interface EntitlementFlags {
  hasSpendingTools: boolean;
  hasDebtTools: boolean;
  /** Any paid key — data connections come with the one product. */
  isDataConnect: boolean;
  /** Any active paid subscription (the same as isDataConnect, hasSpendingTools
   *  and hasDebtTools: one product). */
  isPremium: boolean;
  /** @deprecated legacy tier check; no active holders. Preserved for any
   *  existing early_access-gated surface. */
  isEarlyAccess: boolean;
  highestEntitlement: EntitlementId | null;
}

/**
 * Pure derivation of the client entitlement flags. Used by SubscriptionContext
 * and DemoProviders so the mapping can't drift between them.
 */
export const deriveEntitlementFlags = (
  entitlementIds: readonly string[],
): EntitlementFlags => {
  const ids = [...entitlementIds];
  const spending = hasSpendingTools(ids);
  const debt = hasDebtTools(ids);
  const dataConnect = spending || debt;
  return {
    hasSpendingTools: spending,
    hasDebtTools: debt,
    isDataConnect: dataConnect,
    isPremium: dataConnect,
    isEarlyAccess: ids.includes('early_access'),
    highestEntitlement: highestEntitlement(ids),
  };
};
